// Reuse the sibling email pattern so all three auth drawers behave identically.
// EMAIL_PATTERN lives on the sign-in drawer; re-declare here to avoid a
// cross-drawer import cycle (each drawer owns its constants).
export const REGISTRATION_DRAWER_ID = 'registration-drawer';

export const EMAIL_PATTERN = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
export const NAME_PATTERN = /^[A-Za-z\s'-]+$/;
export const POSTCODE_PATTERN = /^\d{4}$/;
// EWM-826 — RESEND_VERIFICATION_COOLDOWN_MS removed: registration is now
// asynchronous (Workato sends the verification email once the OneOffice
// business unit is provisioned) and the success panel no longer offers a
// resend affordance. The sign-in drawer's own copy of this constant
// (components/organisms/authentication/sign-in-drawer/constants) is
// unaffected — it backs a separate resend flow shown on AccountUnverifiedError.

export type CountryCode = 'AU' | 'NZ';

// EWM-513 — country is fixed from the site domain/locale (no in-form selector).
// It drives the tax-number label/validator and the address country.
export const TAX_CONFIG: Record<CountryCode, { labelKey: string; placeholderKey: string; maxLength: number }> = {
  AU: { labelKey: 'auth.tax-abn-label', placeholderKey: 'auth.tax-abn-placeholder', maxLength: 14 },
  NZ: { labelKey: 'auth.tax-nzbn-label', placeholderKey: 'auth.tax-nzbn-placeholder', maxLength: 16 },
};

// State/region dropdowns. AU = 8 states/territories (full-name labels with
// abbreviation values, per Dan's v0); NZ = 16 regions (value === label).
export const STATE_OPTIONS: Record<CountryCode, { value: string; label: string }[]> = {
  AU: [
    { value: 'NSW', label: 'New South Wales' },
    { value: 'VIC', label: 'Victoria' },
    { value: 'QLD', label: 'Queensland' },
    { value: 'WA', label: 'Western Australia' },
    { value: 'SA', label: 'South Australia' },
    { value: 'TAS', label: 'Tasmania' },
    { value: 'ACT', label: 'Australian Capital Territory' },
    { value: 'NT', label: 'Northern Territory' },
  ],
  NZ: [
    { value: 'Northland', label: 'Northland' },
    { value: 'Auckland', label: 'Auckland' },
    { value: 'Waikato', label: 'Waikato' },
    { value: 'Bay of Plenty', label: 'Bay of Plenty' },
    { value: 'Gisborne', label: 'Gisborne' },
    { value: "Hawke's Bay", label: "Hawke's Bay" },
    { value: 'Taranaki', label: 'Taranaki' },
    { value: 'Manawatū-Whanganui', label: 'Manawatū-Whanganui' },
    { value: 'Wellington', label: 'Wellington' },
    { value: 'Tasman', label: 'Tasman' },
    { value: 'Nelson', label: 'Nelson' },
    { value: 'Marlborough', label: 'Marlborough' },
    { value: 'West Coast', label: 'West Coast' },
    { value: 'Canterbury', label: 'Canterbury' },
    { value: 'Otago', label: 'Otago' },
    { value: 'Southland', label: 'Southland' },
  ],
};

// Address labels differ by country: AU = Suburb/State, NZ = City/Region.
// Keyed by the in-form Country so labels stay in sync with STATE_OPTIONS.
export const ADDRESS_LABEL_KEYS: Record<CountryCode, { locality: string; region: string; regionPlaceholder: string }> =
  {
    AU: {
      locality: 'auth.register-address-suburb',
      region: 'auth.register-address-state',
      regionPlaceholder: 'auth.register-state-select-placeholder',
    },
    NZ: {
      locality: 'auth.register-address-city',
      region: 'auth.register-address-region',
      regionPlaceholder: 'auth.register-region-select-placeholder',
    },
  };

// Terms/Privacy consent links. EWM-667 — link directly at the current Zendesk
// articles per site locale (AU/NZ) instead of the old `/terms-of-trade` +
// `/privacy-policy` Studio slugs, which only redirected here. The direct URLs
// skip the redirect hop (QA inspects the rendered href, not just the eventual
// destination). `country` is locale-derived (EWM-513). The footer's Privacy/
// Terms links (EWM-529) are out of scope. The "Terms of Trade" → "Terms and
// Conditions" label rename lives in the `auth.register-accept-terms` copy.
export const TERMS_HREF: Record<CountryCode, string> = {
  AU: 'https://help.electusdistribution.com.au/hc/en-us/articles/22321548481305-Terms-Conditions',
  NZ: 'https://help.electusdistribution.co.nz/hc/en-us/articles/22352561860761-Terms-Conditions',
};
export const PRIVACY_HREF: Record<CountryCode, string> = {
  AU: 'https://help.electusdistribution.com.au/hc/en-us/articles/46367223701657-Privacy-Policy',
  NZ: 'https://help.electusdistribution.co.nz/hc/en-us/articles/46367904996505-Privacy-Policy',
};
